'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Copy, Check } from 'lucide-react';
import { AuthMode } from './AuthScreen';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const DEMO_CREDENTIALS = {
  email: 'demo@studymate.app',
  password: 'StudyMate2026!',
};

interface LoginFormProps {
  onSwitch: (mode: AuthMode) => void;
}

export default function LoginForm({ onSwitch }: LoginFormProps) {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const handleCopy = (field: 'email' | 'password') => {
    const val = field === 'email' ? DEMO_CREDENTIALS.email : DEMO_CREDENTIALS.password;
    navigator.clipboard.writeText(val).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    });
  };

  const handleAutofill = () => {
    setValue('email', DEMO_CREDENTIALS.email);
    setValue('password', DEMO_CREDENTIALS.password);
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back!');
      router.push('/dashboard');
    } catch (err: any) {
      const msg = err?.message || 'Sign in failed.';
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials')) {
        toast.error('Invalid email or password. Please try again.');
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div className="animate-slide-up">
      {/* Mobile logo */}
      <div className="lg:hidden flex items-center gap-2 mb-8">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
          <span className="text-white font-bold text-sm">SM</span>
        </div>
        <span className="font-bold text-xl text-foreground">StudyMate</span>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
        <p className="text-muted-foreground text-sm">Sign in to your academic workspace.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Email */}
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium text-foreground mb-1.5">
            Email
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
              errors.email ? 'border-destructive' : 'border-border'
            }`}
            {...register('email', {
              required: 'Email is required.',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-destructive">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="block text-sm font-medium text-foreground">
              Password
            </label>
            <button
              type="button"
              onClick={() => onSwitch('forgot')}
              className="text-xs text-primary hover:underline font-medium"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                errors.password ? 'border-destructive' : 'border-border'
              }`}
              {...register('password', {
                required: 'Password is required.',
                minLength: { value: 6, message: 'Password must be at least 6 characters.' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-destructive">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            id="remember"
            type="checkbox"
            className="w-4 h-4 rounded border-border accent-primary"
            {...register('remember')}
          />
          <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
            Keep me signed in
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Signing in...</span>
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      {/* Switch to signup */}
      <p className="mt-6 text-center text-sm text-muted-foreground">
        New to StudyMate?{' '}
        <button
          onClick={() => onSwitch('signup')}
          className="text-primary font-semibold hover:underline"
        >
          Create an account
        </button>
      </p>

      {/* Demo credentials box */}
      <div className="mt-8 p-4 rounded-xl border border-border bg-muted/40">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Demo Account
          </p>
          <button
            type="button"
            onClick={handleAutofill}
            className="text-xs text-primary font-medium hover:underline"
          >
            Autofill
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 bg-card rounded-lg px-3 py-2 border border-border">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">Email</p>
              <p className="text-xs font-mono text-foreground truncate">{DEMO_CREDENTIALS.email}</p>
            </div>
            <button
              type="button"
              onClick={() => handleCopy('email')}
              className="flex-shrink-0 p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Copy email"
            >
              {copiedField === 'email' ? <Check size={14} className="text-success" /> : <Copy size={14} />}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 bg-card rounded-lg px-3 py-2 border border-border">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">Password</p>
              <p className="text-xs font-mono text-foreground">{DEMO_CREDENTIALS.password}</p>
            </div>
            <button
              type="button"
              onClick={() => handleCopy('password')}
              className="flex-shrink-0 p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Copy password"
            >
              {copiedField === 'password' ? <Check size={14} className="text-success" /> : <Copy size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}